import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { formatTimer } from "./useTimer";

const ANNOUNCEMENT_INTERVAL_MS = 30000;
const CRITICAL_THRESHOLD_SECONDS = 60;
const VALID_TIME_THRESHOLD_SECONDS = 900;

interface TimerProps {
  timeRemaining: number;
  announcementPrefix?: string;
  announcementSuffix?: string;
  isCriticalAnnouncementEnabled?: boolean;
  disableLiveAnnouncements?: boolean;
  announcementKey?: string;
}

/**
 * Manages all screen reader announcement logic for the Timer component.
 * It handles stable 30-second announcements, critical 10-second announcements,
 * and maintains the time reference for consistent reading.
 */
export const useTimerReader = ({
  timeRemaining,
  announcementPrefix,
  announcementSuffix,
  isCriticalAnnouncementEnabled = true,
  disableLiveAnnouncements = false,
  announcementKey,
}: TimerProps) => {
  const { t } = useTranslation("common");

  const { minutes, seconds } = formatTimer(timeRemaining);
  const totalSeconds = Math.floor(timeRemaining / 1000);

  const [srText, setSrText] = useState("");
  const [isTimeValidated, setIsTimeValidated] = useState(false);

  const currentFormattedTimeRef = useRef({ minutes, seconds });

  const prefixString = announcementPrefix ? `${announcementPrefix} ` : "";
  const suffixString = announcementSuffix ? ` ${announcementSuffix}` : "";

  const setIntervalAnnouncement = useCallback(() => {
    const { minutes: currentMins, seconds: currentSecs } =
      currentFormattedTimeRef.current;

    let announcement = "";

    if (announcementKey) {
      announcement = t(announcementKey, {
        minutes: currentMins,
        seconds: currentSecs,
      });
    } else {
      const timeAnnouncement = t(
        "{{minutes}} minutes and {{seconds}} seconds",
        {
          minutes: currentMins,
          seconds: currentSecs,
        }
      );
      announcement = `${prefixString}${timeAnnouncement}${suffixString}`;
    }

    setSrText(announcement);
  }, [prefixString, suffixString, t, announcementKey]);

  // --- Update Ref & Initial Announcement (Runs Every Second) ---
  useEffect(() => {
    currentFormattedTimeRef.current = { minutes, seconds };

    const isValidRange =
      totalSeconds <= VALID_TIME_THRESHOLD_SECONDS && totalSeconds > 0;

    if (isValidRange && !isTimeValidated) {
      setIsTimeValidated(true);
    }

    if (isTimeValidated && !srText) {
      setIntervalAnnouncement();
    }
  }, [
    minutes,
    seconds,
    totalSeconds,
    isTimeValidated,
    srText,
    setIntervalAnnouncement,
  ]);

  // --- Manage 30-Second Interval ---
  useEffect(() => {
    let cleanup;

    if (
      !disableLiveAnnouncements &&
      totalSeconds >= CRITICAL_THRESHOLD_SECONDS
    ) {
      const interval = setInterval(() => {
        setIntervalAnnouncement();
      }, ANNOUNCEMENT_INTERVAL_MS);

      cleanup = () => clearInterval(interval);
    }

    return cleanup;
  }, [setIntervalAnnouncement, disableLiveAnnouncements]);

  // --- Critical Time Threshold Announcements ---
  useEffect(() => {
    if (!isCriticalAnnouncementEnabled) {
      return;
    }

    if (
      totalSeconds > 0 &&
      totalSeconds < CRITICAL_THRESHOLD_SECONDS &&
      totalSeconds % 10 === 0
    ) {
      const { minutes: criticalMins, seconds: criticalSecs } =
        currentFormattedTimeRef.current;

      const criticalAnnouncement = t(
        "Attention! Only {{minutes}} minutes and {{seconds}} seconds left!",
        {
          minutes: criticalMins,
          seconds: criticalSecs,
        }
      );

      setSrText(criticalAnnouncement);
    }
  }, [totalSeconds, t, isCriticalAnnouncementEnabled]);

  return { srText, minutes, seconds };
};
